import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import HowToPlay from "@/components/HowToPlay";
import QuestionExtras from "@/components/QuestionExtras";
import {
  Trophy, ArrowRight, Clock, CheckCircle, XCircle, Shield, Coins, X,
  Crown, ArrowsClockwise, Scales, Sparkle,
} from "@phosphor-icons/react";
import { tick, whoosh } from "@/lib/audio";

const API_ROOT = process.env.REACT_APP_BACKEND_URL || "";

export default function Ladder() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null); // { total_rungs, payouts, safe_rungs, question_seconds }
  const [run, setRun] = useState(null);
  const [question, setQuestion] = useState(null);
  const [rung, setRung] = useState(1);
  const [banked, setBanked] = useState(0);
  const [ended, setEnded] = useState(null);
  const [endsAt, setEndsAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [confirmCashOut, setConfirmCashOut] = useState(false);
  const [lifelinesUsed, setLifelinesUsed] = useState([]);
  const [removed, setRemoved] = useState([]); // option indices hidden by 50:50
  const [aiHint, setAiHint] = useState(null); // {recommended_index, confidence, reason}
  const [localPick, setLocalPick] = useState(null); // buffered pick — not submitted until lock-in or timeout
  const [submitting, setSubmitting] = useState(false);
  const timedOutFiredRef = useRef(false);
  const lastTickAtRef = useRef(100);
  const localPickRef = useRef(null);
  useEffect(() => { localPickRef.current = localPick; }, [localPick]);
  const nkPlus = !!user?.nk_plus_active;

  // Load static ladder structure once
  useEffect(() => {
    api.get("/ladder/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  // Tick clock when a question is active
  useEffect(() => {
    if (!question) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [question]);

  // Time-out handler
  useEffect(() => {
    if (!question || !endsAt) return;
    if (timedOutFiredRef.current) return;
    const remainingMs = new Date(endsAt).getTime() - Date.now();
    const remainingSec = Math.ceil(remainingMs / 1000);
    if (remainingSec > 0 && remainingSec <= 3 && remainingSec !== lastTickAtRef.current) {
      lastTickAtRef.current = remainingSec;
      tick();
    }
    if (remainingMs <= 0) {
      timedOutFiredRef.current = true;
      whoosh();
      // Submit whatever the player had buffered (or -1 for no pick).
      submitAnswer(localPickRef.current ?? -1, true);
    }
  }, [now, question, endsAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRungs = meta?.total_rungs || 15;
  const questionSecs = meta?.question_seconds || 15;
  const payouts = meta?.payouts || {};
  const safeRungs = meta?.safe_rungs || [5, 10];

  const secondsLeft = endsAt
    ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 1000))
    : null;

  const start = async () => {
    try {
      const { data } = await api.post("/ladder/solo/start");
      setRun(data.run_id);
      setQuestion(data.question);
      setRung(data.rung);
      setBanked(data.banked || 0);
      setEnded(null);
      setEndsAt(data.question_ends_at || null);
      setLifelinesUsed(data.lifelines_used || []);
      setRemoved([]);
      setAiHint(null);
      setLocalPick(null);
      timedOutFiredRef.current = false;
      if (data.ladder) setMeta(data.ladder);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start");
    }
  };

  // Buffered tap: select / deselect / change without hitting the server.
  // Server is only called when the player locks in or when time runs out.
  const togglePick = (i) => {
    if (submitting || removed.includes(i)) return;
    setLocalPick((cur) => (cur === i ? null : i));
  };

  const submitAnswer = async (i, isTimeout = false) => {
    if (!run || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/ladder/solo/answer", {
        run_id: run, option_index: isTimeout && i === null ? -1 : i,
      });
      if (data.ended) {
        setEnded({
          correct: data.correct,
          rung_reached: data.rung_reached,
          rungs_cleared: data.rungs_cleared,
          full_clear: data.full_clear,
          season_score_earned: data.season_score_earned,
          rating_award: data.rating_award,
          new_record: data.new_record,
          timed_out: data.timed_out || isTimeout,
          safe_floor: data.safe_floor,
        });
        setRun(null);
        setQuestion(null);
        setEndsAt(null);
        setLocalPick(null);
        return;
      }
      setRung(data.next_rung);
      setBanked(data.banked || data.payout_earned || 0);
      setQuestion(data.question);
      setEndsAt(data.question_ends_at || null);
      setRemoved([]);
      setAiHint(null);
      setLocalPick(null);
      timedOutFiredRef.current = false;
      lastTickAtRef.current = 100;
      toast.success(`Rung ${data.rung_cleared} cleared · banked ${data.payout_earned} SS`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const cashOut = async () => {
    if (!run) return;
    try {
      const { data } = await api.post("/ladder/solo/cash-out", { run_id: run });
      setEnded({
        cashed_out: true,
        rungs_cleared: data.rungs_cleared,
        season_score_earned: data.season_score_earned,
        new_record: data.new_record,
      });
      setRun(null);
      setQuestion(null);
      setEndsAt(null);
      setConfirmCashOut(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cash-out failed");
    }
  };

  const useLifeline = async (kind) => {
    if (!run) return;
    if (!nkPlus) {
      toast("Lifelines are an NK+ perk", {
        description: "Upgrade in Profile → NK+ to unlock 50:50 and Skip.",
      });
      return;
    }
    try {
      const { data } = await api.post("/ladder/solo/lifeline", { run_id: run, kind });
      setLifelinesUsed(data.lifelines_used || []);
      if (data.kind === "fifty") {
        const remove = data.remove_indices || [];
        setRemoved(remove);
        // Drop the buffered pick if it just got hidden by 50:50.
        setLocalPick((cur) => (cur !== null && remove.includes(cur) ? null : cur));
        toast.success("50:50 — two wrong answers removed");
      } else if (data.kind === "ai") {
        setAiHint({
          recommended_index: data.recommended_index,
          confidence: data.confidence,
          reason: data.reason,
        });
        toast.success(`AI Help — recommends option ${String.fromCharCode(65 + data.recommended_index)}`);
      } else {
        setQuestion(data.question);
        setEndsAt(data.question_ends_at || null);
        setRemoved([]);
        setAiHint(null);
        setLocalPick(null);
        timedOutFiredRef.current = false;
        lastTickAtRef.current = 100;
        toast.success("Question skipped — fresh 15 seconds");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lifeline failed");
    }
  };

  // Visual ladder column (used in active gameplay)
  const renderLadder = () => (
    <div className="space-y-1" data-testid="ladder-visual">
      {Array.from({ length: totalRungs }, (_, i) => totalRungs - i).map((r) => {
        const isCurrent = r === rung;
        const isCleared = r < rung;
        const isSafe = safeRungs.includes(r);
        const isFull = r === totalRungs;
        return (
          <div
            key={r}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs font-mono transition-colors ${
              isCurrent ? "bg-nk-accent/20 text-nk-accent border border-nk-accent/50" :
              isCleared ? "bg-nk-surface/50 text-nk-mute" :
              "text-nk-dim"
            }`}
          >
            <span className="w-6 text-right opacity-60">{r}</span>
            {isFull
              ? <Trophy size={11} weight={isCleared ? "fill" : "duotone"} className="text-nk-gold" />
              : isSafe
                ? <Shield size={11} weight={isCleared ? "fill" : "duotone"} className={isCleared ? "text-nk-accent" : "text-nk-gold/70"} />
                : <Coins size={11} weight="duotone" className="text-nk-mute/50" />}
            <span className="flex-1">{payouts[r]} SS</span>
            {isSafe && !isFull && <span className="text-[9px] uppercase tracking-wider opacity-60">SAFE</span>}
          </div>
        );
      })}
    </div>
  );

  // ====== ACTIVE GAMEPLAY ======
  if (question) {
    const pct = secondsLeft != null ? (secondsLeft / questionSecs) * 100 : 100;
    const isUrgent = secondsLeft != null && secondsLeft <= 3;

    return (
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">

          {/* MAIN PLAY AREA */}
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="eyebrow">Rung {rung} of {totalRungs} · {question.difficulty}</p>
              <div className="flex items-center gap-4 font-mono text-sm">
                <span className="text-nk-mute">Banked</span>
                <span className="text-nk-accent" data-testid="ladder-banked">{banked} SS</span>
              </div>
            </div>

              {/* Timer bar */}
              <div className="space-y-1.5" data-testid="ladder-timer">
                <div className="flex items-center justify-between text-[11px] font-mono">
                  <span className="flex items-center gap-1 text-white/55">
                    <Clock size={11} weight="duotone" /> Time
                  </span>
                  <span className={`tabular-nums font-display ${
                    secondsLeft != null && secondsLeft <= 3 ? "text-nk-bad" :
                    secondsLeft != null && secondsLeft <= 5 ? "text-nk-warn" : "text-nk-gold"
                  }`}>
                    {secondsLeft != null ? `${secondsLeft}s` : "—"}
                  </span>
                </div>
                <div className="h-1.5 nk-timer-bar rounded-full overflow-hidden">
                  <div
                    className={`h-full nk-timer-fill ${
                      secondsLeft != null && secondsLeft <= 3 ? "nk-timer-urgent" :
                      secondsLeft != null && secondsLeft <= 5 ? "nk-timer-warn" : ""
                    }`}
                    style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                  />
                </div>
              </div>

              <div className="nk-board rounded-md p-5 sm:p-8">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-nk-gold/70 font-mono">{question.category}</p>
                  <p className="text-xs text-nk-gold font-mono">Win {question.payout} SS</p>
                </div>
                <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-5 text-white">{question.body}</p>

                <QuestionExtras extras={question.nke_extras} qid={question.qid} apiUrl={API_ROOT} />

                {/* NK+ Lifelines — visible to everyone, locked for free players */}
                <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="ladder-lifelines">
                  <LifelineBtn
                    kind="fifty" label="50:50" Icon={Scales}
                    used={lifelinesUsed.includes("fifty")}
                    locked={!nkPlus}
                    onUse={useLifeline}
                  />
                  <LifelineBtn
                    kind="ai" label="AI Help" Icon={Sparkle}
                    used={lifelinesUsed.includes("ai")}
                    locked={!nkPlus}
                    onUse={useLifeline}
                  />
                  <LifelineBtn
                    kind="skip" label="Skip" Icon={ArrowsClockwise}
                    used={lifelinesUsed.includes("skip")}
                    locked={!nkPlus}
                    onUse={useLifeline}
                  />
                  {!nkPlus && (
                    <span className="text-[10px] text-nk-gold inline-flex items-center gap-1" data-testid="ladder-lifelines-upsell">
                      <Crown size={10} weight="fill" /> NK+ perk
                    </span>
                  )}
                </div>

                {aiHint && (
                  <div className="mb-4 p-3 rounded-md border border-nk-gold/40 bg-nk-gold/[0.06] flex items-start gap-2"
                       data-testid="ladder-ai-hint">
                    <Sparkle size={16} weight="fill" className="text-nk-gold shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-nk-gold font-mono">
                        AI recommends option {String.fromCharCode(65 + aiHint.recommended_index)}
                        {aiHint.confidence != null && (
                          <span className="opacity-70"> · {Math.round(aiHint.confidence * 100)}% confidence</span>
                        )}
                      </p>
                      {aiHint.reason && <p className="text-[11px] text-white/70 mt-1">{aiHint.reason}</p>}
                    </div>
                  </div>
                )}

                <div className="grid gap-3">
                  {question.options.map((opt, i) => {
                    const isRemoved = removed.includes(i);
                    const isSelected = localPick === i;
                    const isAiRec = aiHint?.recommended_index === i && !isRemoved;
                    return (
                      <button
                        key={i}
                        data-testid={`ladder-option-btn-${i}`}
                        onClick={() => togglePick(i)}
                        disabled={isRemoved || submitting}
                        aria-pressed={isSelected}
                        className={`nk-option text-left rounded-md px-4 py-4 flex items-center gap-3 ${
                          isRemoved ? "nk-option-dimmed line-through cursor-not-allowed" :
                          isSelected ? "nk-option-selected" :
                          isAiRec ? "ring-1 ring-nk-gold/50" : ""
                        }`}
                      >
                        <span className={`font-mono text-xs w-5 ${isSelected ? "text-nk-gold" : "text-white/45"}`}>
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className="flex-1 text-sm sm:text-base">{opt}</span>
                        {isAiRec && !isSelected && (
                          <Sparkle size={14} weight="fill" className="text-nk-gold/70 shrink-0" />
                        )}
                        {isSelected && <CheckCircle size={18} weight="fill" className="text-nk-gold shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Lock-in row */}
                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-white/55" data-testid="ladder-pick-hint">
                    {localPick === null
                      ? "Pick an answer. You can change it until time runs out."
                      : <>You picked <span className="font-mono text-nk-gold">{String.fromCharCode(65 + localPick)}</span>. Tap to change, or lock in.</>}
                  </p>
                  <button
                    type="button"
                    data-testid="ladder-lockin-btn"
                    onClick={() => submitAnswer(localPick, false)}
                    disabled={localPick === null || submitting}
                    className="nk-btn nk-btn-gold disabled:opacity-40 disabled:cursor-not-allowed text-xs px-4 py-2"
                  >
                    Lock in <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>

            {/* Cash-out button (visible from rung 2 onwards = after first correct answer) */}
            {banked > 0 && (
              <button
                data-testid="ladder-cash-out-btn"
                onClick={() => setConfirmCashOut(true)}
                className="w-full border border-nk-gold text-nk-gold hover:bg-nk-gold/10 px-4 py-3 rounded-sm transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                <Coins size={16} weight="duotone" /> Cash out with {banked} SS
              </button>
            )}
          </div>

          {/* SIDEBAR: visual ladder */}
          <aside className="hidden lg:block">
            <p className="eyebrow mb-3">Prize Ladder</p>
            {renderLadder()}
          </aside>
        </div>

        {/* Cash-out confirmation modal */}
        {confirmCashOut && (
          <div className="fixed inset-0 z-50 bg-nk-bg/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmCashOut(false)}>
            <div className="nk-card max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="ladder-cash-out-modal">
              <button onClick={() => setConfirmCashOut(false)} className="absolute top-3 right-3 text-nk-mute hover:text-nk-text">
                <X size={16} />
              </button>
              <Coins size={28} weight="duotone" className="text-nk-gold mb-4" />
              <p className="eyebrow mb-2">Cash out?</p>
              <p className="font-display text-2xl font-medium tracking-tight mb-2">
                Walk away with <span className="text-nk-accent">{banked} SS</span>?
              </p>
              <p className="text-sm text-nk-mute mb-6">
                You&apos;ll end the climb at rung {rung} and keep what you&apos;ve won.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmCashOut(false)}
                  className="flex-1 border border-nk-line px-4 py-2.5 rounded-sm hover:bg-nk-surface text-sm"
                >Keep climbing</button>
                <button
                  data-testid="ladder-cash-out-confirm"
                  onClick={cashOut}
                  className="flex-1 bg-nk-gold text-nk-bg px-4 py-2.5 rounded-sm font-medium hover:bg-nk-gold/90 text-sm"
                >Yes, cash out</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ====== END SCREEN ======
  if (ended) {
    const isFullClear = ended.full_clear;
    const isCashOut = ended.cashed_out;
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="ladder-end-screen">
        {isFullClear
          ? <Trophy size={32} weight="duotone" className="text-nk-gold" />
          : isCashOut
            ? <Coins size={32} weight="duotone" className="text-nk-gold" />
            : ended.correct
              ? <CheckCircle size={32} weight="duotone" className="text-nk-accent" />
              : <XCircle size={32} weight="duotone" className="text-nk-bad" />}
        <p className="eyebrow">
          {isFullClear ? "Full clear · top of the ladder"
            : isCashOut ? "Cashed out"
            : ended.timed_out ? "Climb ended · timed out"
            : "Climb ended"}
        </p>
        <h1 className="font-display text-4xl font-medium tracking-tight">
          {isFullClear
            ? "You cleared all 15 rungs."
            : isCashOut
              ? <>You walked away from rung <span className="text-nk-accent">{ended.rungs_cleared + 1}</span>.</>
              : <>You cleared <span className="text-nk-accent">{ended.rungs_cleared}</span> rungs.</>}
        </h1>
        {ended.new_record && <p className="text-nk-accent text-sm">★ New personal best!</p>}
        {ended.safe_floor != null && ended.safe_floor > 0 && !isFullClear && !isCashOut && (
          <p className="text-sm text-nk-gold inline-flex items-center gap-1">
            <Shield size={12} weight="fill" /> Safe rung saved you — you keep {ended.safe_floor} SS.
          </p>
        )}
        <p className="text-nk-mute">
          You earned <span className="text-nk-accent font-mono">+{ended.season_score_earned}</span> Season Score
          {ended.rating_award ? <> · <span className="text-nk-accent font-mono">+{ended.rating_award}</span> Rating</> : null}.
        </p>
        <button
          data-testid="ladder-restart-btn"
          onClick={start}
          className="inline-flex items-center gap-2 bg-nk-accent text-nk-bg font-medium px-6 py-3 rounded-sm hover:bg-nk-accentSoft transition-colors"
        >Climb again <ArrowRight size={14} weight="bold" /></button>
      </div>
    );
  }

  // ====== START SCREEN ======
  return (
    <div className="max-w-3xl space-y-7">
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/80 font-medium mb-2">NK Ladder</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          Climb. Cash out. <span className="italic font-light text-nk-gold">Or push.</span>
        </h1>
        <p className="text-sm sm:text-base text-white/65 mt-3 max-w-lg leading-relaxed">
          15 rungs, one question at a time. Bank what you&apos;ve earned at any point — or risk it for the next rung.
        </p>
      </header>

      <button
        data-testid="ladder-start-btn"
        onClick={start}
        className="nk-btn nk-btn-gold px-7 py-4 text-base"
      >
        Start climbing <ArrowRight size={16} weight="bold" />
      </button>

      <HowToPlay
        title="How NK Ladder works"
        steps={[
          { title: "One climb at a time", desc: "15 questions, 15 rungs. Each rung is harder than the last." },
          { title: "Cash out whenever", desc: "Tap cash out to keep what you've banked. Or push for the next rung." },
          { title: "Safe rungs at 5 and 10", desc: "Get past rung 5 or 10 and that floor is locked in if you miss." },
        ]}
        points={[
          { label: "Per rung", value: "Up to +500 SS" },
          { label: "Time / question", value: `${questionSecs}s` },
          { label: "Safe floors", value: "Rungs 5 & 10" },
          { label: "Full clear bonus", value: "+1,000 SS" },
        ]}
      />

      {/* Prize Ladder preview — small, collapsed by default to keep the screen calm */}
      <PrizeLadderPreview meta={meta} totalRungs={totalRungs} safeRungs={safeRungs} payouts={payouts} />
    </div>
  );
}

function PrizeLadderPreview({ meta, totalRungs, safeRungs, payouts }) {
  const [open, setOpen] = useState(false);
  if (!meta) return null;
  return (
    <div className="nk-card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ladder-prize-toggle"
        className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-left hover:bg-white/5 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-white">
          <Trophy size={14} weight="duotone" className="text-nk-gold" />
          Prize ladder
        </span>
        <span className="text-[10px] text-white/40 font-mono">Top: {payouts[totalRungs]} SS</span>
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 space-y-1">
          {Array.from({ length: totalRungs }, (_, i) => totalRungs - i).map((r) => {
            const isSafe = safeRungs.includes(r);
            const isFull = r === totalRungs;
            return (
              <div key={r}
                   className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs font-mono ${
                     isFull ? "bg-nk-gold/10 text-nk-gold border border-nk-gold/30"
                     : isSafe ? "bg-nk-gold/5 text-white border border-nk-gold/20"
                     : "text-white/55"
                   }`}>
                <span className="w-6 text-right opacity-60">{r}</span>
                {isFull
                  ? <Trophy size={11} weight="duotone" className="text-nk-gold" />
                  : isSafe
                    ? <Shield size={11} weight="duotone" className="text-nk-gold/70" />
                    : <Coins size={11} weight="duotone" className="text-white/30" />}
                <span className="flex-1">{payouts[r]} SS</span>
                {isSafe && !isFull && <span className="text-[9px] uppercase tracking-wider opacity-60">SAFE</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LifelineBtn({ kind, label, Icon, used, locked, onUse }) {
  return (
    <button
      data-testid={`ladder-lifeline-${kind}`}
      onClick={() => !used && onUse(kind)}
      disabled={used}
      title={locked ? "NK+ members only" : used ? "Already used this climb" : label}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        used
          ? "border-nk-line/30 text-nk-dim/50 line-through cursor-not-allowed"
          : locked
            ? "border-nk-gold/40 text-nk-gold/70 hover:bg-nk-gold/10"
            : "border-nk-gold text-nk-gold hover:bg-nk-gold/10"
      }`}
    >
      <Icon size={12} weight="duotone" />
      {label}
      {locked && !used && <Crown size={10} weight="fill" className="opacity-80" />}
    </button>
  );
}
